/**
 * React Query hooks for the Keyword Search feature.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import { useMemo } from 'react';
import {
  kwRequestNeedsAdmin, kwSearchIdentity, sanitizeKwRequestForNonAdmin,
} from '../pages/keywordRequest';
import { useDbUser } from './useDbUser';
import type { DrugSort } from '../utils/drugResultsTable';
import type {
  KeywordSearchRequest,
  KeywordSearchResponse,
  KeywordSearchResultItem,
  KeywordSearchResponseUnion,
  KeywordSectionOption,
  SynonymExpansionResponse,
} from '../types';

/** Near on-demand fetch mode: rows come back without highlights, sorted server-side. */
export type FetchOpts = { passages: 'on_demand'; sort: DrugSort | null };

function withFetchOpts(request: KeywordSearchRequest, opts?: FetchOpts): KeywordSearchRequest {
  if (!opts) return request;
  return { ...request, passages: opts.passages, ...(opts.sort ? { sort: opts.sort } : {}) };
}

/**
 * THE non-admin choke point for keyword requests (spec 2026-10-16 §6a; K1b
 * review). Every keyword search hook sends ``useAdminSafeRequest(request)``,
 * never the raw request, so no caller (SearchPage, Compare, a restored
 * sessionStorage handoff) can send an admin-only request for a non-admin:
 *   - request without admin-only fields (document_engine, sba /
 *     fda_approval_letter; a legacy ``['fda_letter']`` without the flag is
 *     NOT admin-only) -> unchanged;
 *   - admin status still loading (/auth/sync) -> null (not sent yet);
 *   - settled admin -> unchanged; settled non-admin -> the non-admin shape
 *     (``sanitizeKwRequestForNonAdmin``: Labels default).
 */
export function useAdminSafeRequest(request: KeywordSearchRequest | null): KeywordSearchRequest | null {
  const { isAdmin, loading } = useDbUser();
  return useMemo(() => {
    if (!request || !kwRequestNeedsAdmin(request)) return request;
    if (loading) return null;
    return isAdmin ? request : sanitizeKwRequestForNonAdmin(request);
  }, [request, isAdmin, loading]);
}

/**
 * Query hook for keyword searches with React Query caching.
 * Results survive navigation (back button restores cached results instantly).
 *
 * Returns the discriminated union ``KeywordSearchResponseUnion`` — callers
 * narrow on ``response.kind === 'letters'`` to dispatch between the
 * drug-shaped (``KeywordSearchResults``) and letter-shaped
 * (``LetterResultsTable``) renderers.  The Phase G letter corpus relies
 * on this discrimination; before Task 13 the cast was hardcoded to the
 * drug-shaped variant.
 */
export function useKeywordSearchQuery(
  rawRequest: KeywordSearchRequest | null,
  opts?: FetchOpts,
) {
  const request = useAdminSafeRequest(rawRequest);
  return useQuery<KeywordSearchResponseUnion | null>({
    queryKey: ['keyword-search', request, opts?.passages ?? null, opts?.sort ?? null],
    queryFn: async () => {
      if (!request) return null;
      const body = withFetchOpts(request, opts);
      const response = await authApi.keywordSearch(body as unknown as Record<string, unknown>);
      return response as KeywordSearchResponseUnion;
    },
    enabled: request !== null,
    staleTime: 5 * 60 * 1000,       // 5 min — cached results shown instantly on back nav
    gcTime: 30 * 60 * 1000,         // 30 min — keep inactive cache entries
    refetchOnWindowFocus: false,
  });
}

/**
 * Infinite (paged) keyword search — used by the Compare rail so the user can
 * scroll through ALL results without leaving Compare. Pages by ``offset`` using
 * the same backend pagination as the results page.
 */
export function useKeywordSearchInfinite(
  rawRequest: KeywordSearchRequest | null,
  pageSize: number,
  opts?: FetchOpts,
) {
  const request = useAdminSafeRequest(rawRequest);
  const queryClient = useQueryClient();
  const result = useInfiniteQuery<KeywordSearchResponseUnion | null>({
    queryKey: ['keyword-search-infinite', request, pageSize, opts?.passages ?? null, opts?.sort ?? null],
    initialPageParam: 0,
    // P12: the attempts are bounded by the queryFn itself (max 2 page fetches +
    // 2 passages fetches); React Query's default retry would multiply them.
    retry: false,
    queryFn: async ({ pageParam }) => {
      if (!request) return null;
      const fetchPage = async () => {
        const req = { ...withFetchOpts(request, opts), offset: pageParam as number, limit: pageSize };
        return (await authApi.keywordSearch(req as unknown as Record<string, unknown>)) as KeywordSearchResponseUnion;
      };
      const page = await fetchPage();
      if (!opts) return page;
      const withPassages = async (p: KeywordSearchResponse): Promise<KeywordSearchResponse> => {
        const ids = p.results.map((r) => r.drug_id);
        const got = await queryClient.fetchQuery(keywordPassagesQueryOptions(request, ids));
        return { ...p, results: mergePassages(p.results, got) };
      };
      const needsPassages = (p: KeywordSearchResponseUnion): p is KeywordSearchResponse =>
        p.kind === 'drugs' && p.passages_on_demand === true && p.results.length > 0;
      if (!needsPassages(page)) return page;
      try {
        return await withPassages(page);
      } catch (err) {
        if (!isPassagesUnavailable(err)) throw err;
      }
      const again = await fetchPage();
      if (!needsPassages(again)) return again; // inline: the mode-flip effect handles it
      return await withPassages(again); // a second failure throws -> query error state
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.kind !== 'drugs') return undefined;
      return lastPage.results.length >= pageSize ? allPages.length * pageSize : undefined;
    },
    enabled: request !== null,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // ``request`` = the gated request the query is keyed on (null while held):
  // callers that reset / remove this query must use it, never their raw
  // request (a sanitized non-admin replay lives under a different key).
  return Object.assign(result, { request });
}

export const KW_PASSAGES_STALE_MS = 5 * 60 * 1000;

/** The server could not produce passages (409 / 503 / partial DROP). The caller
 *  refetches the page (P12); nothing is ever merged as empty. */
export class PassagesUnavailableError extends Error {
  constructor(message = 'passages unavailable') {
    super(message);
    this.name = 'PassagesUnavailableError';
  }
}

export function isPassagesUnavailable(err: unknown): boolean {
  if (err instanceof PassagesUnavailableError) return true;
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 409 || status === 503;
}

/**
 * Shared options for fetching passages of specific drugs (P13): Expand
 * (useQuery), Export and the rail (fetchQuery) all use this one key.
 * ``retry: false`` is explicit: the app default is retry: 1, and a 409/503 is
 * handled by the caller, never by React Query retrying. Call sites using
 * useQuery must spread this into a literal object with their own staleTime.
 */
export function keywordPassagesQueryOptions(request: KeywordSearchRequest, ids: number[]) {
  return {
    queryKey: ['keyword-passages', kwSearchIdentity(request), ids] as const,
    queryFn: async (): Promise<KeywordSearchResponse> => {
      const { sort: _sort, limit: _limit, offset: _offset, ...rest } = request;
      void _sort; void _limit; void _offset;
      const body = { ...rest, passages: 'on_demand', passages_for: ids };
      const response = (await authApi.keywordSearch(body as unknown as Record<string, unknown>)) as KeywordSearchResponse;
      if (response.passages_on_demand !== true || (response.unavailable_corpora?.length ?? 0) > 0) {
        throw new PassagesUnavailableError();
      }
      return response;
    },
    staleTime: KW_PASSAGES_STALE_MS,
    gcTime: 30 * 60 * 1000,
    retry: false as const,
    refetchOnWindowFocus: false,
  };
}

/** Overlay passages onto rows by drug_id: replaces only highlights,
 *  total_matches, best_rank and matches_by_source; rows absent from the
 *  response are kept as-is. */
export function mergePassages(
  rows: KeywordSearchResultItem[],
  passages: Pick<KeywordSearchResponse, 'results'>,
): KeywordSearchResultItem[] {
  const byId = new Map(passages.results.map((r) => [r.drug_id, r]));
  return rows.map((row) => {
    const p = byId.get(row.drug_id);
    if (!p) return row;
    const { matches_by_source: _stale, ...rest } = row;
    void _stale;
    return {
      ...rest,
      highlights: p.highlights,
      total_matches: p.total_matches,
      best_rank: p.best_rank,
      ...(p.matches_by_source ? { matches_by_source: p.matches_by_source } : {}),
    };
  });
}

/**
 * Query hook for section dropdown options (cached).
 * These rarely change so we cache aggressively.
 */
export function useKeywordSearchSections() {
  return useQuery<KeywordSectionOption[]>({
    queryKey: ['keyword-search-sections'],
    queryFn: async () => {
      const response = await authApi.getKeywordSearchSections();
      return response as KeywordSectionOption[];
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

/**
 * Mutation hook for synonym expansion preview.
 */
export function useSynonymExpansion() {
  return useMutation<SynonymExpansionResponse, Error, string>({
    mutationFn: async (keyword) => {
      const response = await authApi.expandSynonyms(keyword);
      return response as SynonymExpansionResponse;
    },
  });
}
